import { watch } from 'vue';
import { useSubscriptionStore } from '@/core/store/useSubscriptionStore';
import { useUserStore } from '@/core/store/useUserStore';
import { featureFlags } from '@/config/featureFlags';
import { i18n } from '@/i18n';
import {
    isReminderInCooldown,
    markReminderShown,
    seedReminderCooldown
} from '@/core/billing/billingNoticeFrequency';

// route-entry 评估时，若 store 超过此时间未 sync 则补一次 sync（catch 消费状态变化）
const STALE_SYNC_MS = 10 * 60 * 1000;

// 模块级会话状态
let initialized = false;
let grantPopupShownThisSession = false;
let reminderInFlight = false;

// 组件外（router guard / watcher）取 i18n 文案的单一来源，与组件内 useI18n 共享同一权威源。
const t = (key, params) => i18n.global.t(key, params);

const dispatchBillingNotice = (detail) => {
    if (typeof window === 'undefined') return;
    try {
        window.dispatchEvent(new CustomEvent('billing:notice', { detail }));
    } catch (e) {
        console.warn('[billing-notice] dispatch failed:', e);
    }
};

const resolveUploadRedirect = () => {
    // 复用现有路由约定，落地到 upload（与 post-auth 默认落地页一致）
    return '/upload';
};

/** 首次发放弹窗：由 store watcher 触发 */
const showTrialGrantedNotice = () => {
    if (grantPopupShownThisSession) return;
    if (!featureFlags.trialCreditsNoticeEnabled) return;

    const store = useSubscriptionStore();
    const userStore = useUserStore();
    const userId = userStore.userInfo?.id;

    grantPopupShownThisSession = true;

    // 从已归一化的 store 读取发放量（normalizeCreditQuota 产物）
    const granted = store.creditQuota?.trial?.granted;
    const hasValidNumber = Number.isFinite(granted) && granted > 0;

    const variant = hasValidNumber ? 'trialCreditsGranted' : 'trialCreditsGrantedGeneric';
    dispatchBillingNotice({
        reasonCode: 'trial_credits_granted',
        title: t(`billing.notices.${variant}.title`),
        message: hasValidNumber
            ? t(`billing.notices.${variant}.message`, { granted })
            : t(`billing.notices.${variant}.message`),
        primaryLabel: t(`billing.notices.${variant}.primaryLabel`),
        secondaryLabel: t(`billing.notices.${variant}.secondaryLabel`),
        redirect: resolveUploadRedirect()
    });

    // 决策默认项：首次弹窗种入提醒冷却，避免次日冗余提醒
    if (userId) seedReminderCooldown(userId);
};

/** 提醒评估：读取 store + 频控 */
export const maybeShowReminder = () => {
    if (!featureFlags.trialCreditsNoticeEnabled) return;
    if (reminderInFlight) return;

    const store = useSubscriptionStore();
    const userStore = useUserStore();
    const userId = userStore.userInfo?.id;
    if (!userId) return;

    // 严格三态判断：creditConsumptionExists 必须为 false（null=未 sync 过，不弹）
    if (store.creditConsumptionExists !== false) return;
    // 余额为 0 不提醒（granted=0 等场景）
    const total = store.credits?.total;
    if (!Number.isFinite(total) || total <= 0) return;
    // 频控：3 天内已展示过则跳过
    if (isReminderInCooldown(userId)) return;

    reminderInFlight = true;

    dispatchBillingNotice({
        reasonCode: 'credit_reminder',
        title: t('billing.notices.creditReminder.title'),
        message: t('billing.notices.creditReminder.message'),
        primaryLabel: t('billing.notices.creditReminder.primaryLabel'),
        secondaryLabel: t('billing.notices.creditReminder.secondaryLabel'),
        redirect: resolveUploadRedirect()
    });
    markReminderShown(userId);

    // 释放 in-flight（下一 tick，防止同次路由连发）
    setTimeout(() => { reminderInFlight = false; }, 0);
};

/** route-entry 评估：鉴权路由 afterEach 调用 */
export const evaluateOnRouteEntry = async () => {
    if (!featureFlags.trialCreditsNoticeEnabled) return;
    const userStore = useUserStore();
    if (!userStore.isLoggedIn) return;

    const store = useSubscriptionStore();
    const lastSyncedAt = store.lastSyncedAt ? new Date(store.lastSyncedAt).getTime() : 0;
    const stale = !lastSyncedAt || Date.now() - lastSyncedAt > STALE_SYNC_MS;

    if (stale) {
        try {
            await store.syncStatus();
            // 首次发放由 watcher 捕获（若本次 sync 返回 true）
        } catch (e) {
            return; // sync 失败不弹窗，沿用 billing 失败隔离
        }
    }
    maybeShowReminder();
};

/** 初始化：启动 store watcher。在 App.vue setup 调用一次 */
export const initBillingNoticeManager = () => {
    if (initialized) return;
    initialized = true;
    const store = useSubscriptionStore();
    // 监听首次发放翻转：false/null → true
    watch(
        () => store.initialTrialCreditsGranted,
        (v) => {
            if (v === true) showTrialGrantedNotice();
        }
    );
};

/** 登出时重置会话级标志（localStorage 频控保留，同用户再登录尊重冷却） */
export const resetBillingNoticeSession = () => {
    grantPopupShownThisSession = false;
    reminderInFlight = false;
};

// 导出内部状态读取，便于单测断言会话级防重与 in-flight 行为（不破坏封装，仅供测试）。
export const __test = {
    get grantPopupShownThisSession() { return grantPopupShownThisSession; },
    get reminderInFlight() { return reminderInFlight; },
    setGrantPopupShownThisSession(v) { grantPopupShownThisSession = v; },
    setReminderInFlight(v) { reminderInFlight = v; },
    setInitialized(v) { initialized = v; }
};
